export interface PreparedImage {
  dataUrl?: string;
  error?: string;
}

export interface PrepareOptions {
  /** Longest edge, in pixels, of the stored image. */
  maxDimension?: number;
  /** JPEG quality between 0 and 1. */
  quality?: number;
  /** Stop re-encoding once the stored string is under this many characters. */
  targetBytes?: number;
}

const maxSourceBytes = 25_000_000;

/**
 * Photos straight off a phone are far too large to keep in browser storage, so
 * uploads are downscaled and re-encoded before they are saved, then re-encoded
 * again at lower quality if the result is still heavy. A 6 MB shot lands around
 * 120 KB, which keeps a full gallery inside the browser's storage limit.
 */
export async function prepareImageUpload(file: File, options: PrepareOptions = {}): Promise<PreparedImage> {
  const maxDimension = options.maxDimension ?? 1400;
  const quality = options.quality ?? 0.74;
  const targetBytes = options.targetBytes ?? 240_000;

  if (!file.type.startsWith('image/')) {
    return { error: 'Choose a PNG, JPG, or WebP image.' };
  }
  if (file.size > maxSourceBytes) {
    return { error: 'That photo is very large. Try one under 25 MB, or paste a hosted image URL.' };
  }

  try {
    const source = await loadImage(await readAsDataUrl(file));
    let dimension = maxDimension;
    let currentQuality = quality;
    let dataUrl = render(source, dimension, currentQuality);

    // Step down until the encoded string is small enough to store reliably.
    let attempts = 0;
    while (dataUrl.length > targetBytes && attempts < 4) {
      attempts += 1;
      dimension = Math.round(dimension * 0.8);
      currentQuality = Math.max(0.55, currentQuality - 0.06);
      dataUrl = render(source, dimension, currentQuality);
    }

    if (!dataUrl || dataUrl.length < 32) {
      return { error: 'Could not read that image. Try a different file.' };
    }
    return { dataUrl };
  } catch {
    return { error: 'Could not read that image. Try a different file.' };
  }
}

function render(source: HTMLImageElement, maxDimension: number, quality: number): string {
  const scale = Math.min(1, maxDimension / Math.max(source.width, source.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  const context = canvas.getContext('2d');
  if (!context) return '';
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality);
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => (typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('read')));
    reader.onerror = () => reject(new Error('read'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('decode'));
    image.src = src;
  });
}
